import React, { useState, useEffect } from 'react';
import { ethers, BrowserProvider, parseUnits, zeroPadValue, toBeHex } from 'ethers';
// viem imports removed as we're using simpler MetaMask API approach for now
import './App.css';

interface WalletState {
  connected: boolean;
  address: string;
  network: string;
  provider: BrowserProvider | null;
  signer: ethers.Signer | null;
}

interface TransactionTemplate {
  target: string;
  data: string;
}

interface FormData {
  amountIn: string;
  minAmountOut: string;
  transactionA: TransactionTemplate;
  transactionB: TransactionTemplate;
}

const BSC_CHAIN_ID = 56;
const BSC_RPC_URL = 'https://bsc-dataseed.binance.org/';

// 支持的账户抽象钱包类型
const SUPPORTED_AA_WALLETS = [
  'Safe', // Gnosis Safe
  'Argent',
  'Ambire',
  'Biconomy',
  'Stackup'
];

function App() {
  const [wallet, setWallet] = useState<WalletState>({
    connected: false,
    address: '',
    network: '',
    provider: null,
    signer: null
  });

  const [formData, setFormData] = useState<FormData>({
    amountIn: '',
    minAmountOut: '',
    transactionA: {
      target: '0xb300000b72DEAEb607a12d5f54773D1C19c7028d',
      data: '0x810c705b000000000000000000000000ce57c3984a549f28b5173ebae96d3e662f3760a70000000000000000000000005efc784d444126ecc05f22c49ff3fbd7d9f4868a00000000000000000000000055d398326f99059ff775485246999027b3197955{amountIn}000000000000000000000000b994882a1b9bd98a71dd6ea5f61577c42848b0e8{minAmountOut}00000000000000000000000000000000000000000000000000000000000000e000000000000000000000000000000000000000000000000000000000000004049aa90356000000000000000000000000000000000000000000000000000000000000000000000000000000000000000055d398326f99059ff775485246999027b3197955000000000000000000000000b994882a1b9bd98a71dd6ea5f61577c42848b0e8{minAmountOut}0000000000000000000000000000000000000000000000000000019990f0771f0000000000000000000000000000000000000000000000000000000000000100000000000000000000000000000000000000000000000000000000000000014000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000001{amountIn}000000000000000000000000000000000000000000000000000000000000000100000000000000000000000000000000000000000000000000000000000000200000000000000000000000000000000000000000000000000000000000000001000000000000000000000000000000000000000000000000000000000000002000000000000000000000000000000000000000000000000000000000000000a000000000000000000000000000000000000000000000000000000000000000e00000000000000000000000000000000000000000000000000000000000000120000000000000000000000000000000000000000000000000000000000000016000000000000000000000000055d398326f99059ff775485246999027b31979550000000000000000000000000000000000000000000000000000000000000001020000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000010000000000000000000000005efc784d444126ecc05f22c49ff3fbd7d9f4868a0000000000000000000000000000000000000000000000000000000000000001000000000000000000002710cf39e58ed4decc9ef7bdb2f9335602dc22be99b4000000000000000000000000000000000000000000000000000000000000000100000000000000000000000000000000000000000000000000000000000000200000000000000000000000000000000000000000000000000000000000000080000000000000000000000000000000000000000000000000000000000000000000000000000000000000000055d398326f99059ff775485246999027b3197955000000000000000000000000b994882a1b9bd98a71dd6ea5f61577c42848b0e8000000000000000000000000000000000000000000000000000000000000006400000000000000000000000000000000000000000000000000000000'
    },
    transactionB: {
      target: '0xb300000b72DEAEb607a12d5f54773D1C19c7028d',
      data: '0x810c705b000000000000000000000000ce57c3984a549f28b5173ebae96d3e662f3760a70000000000000000000000005efc784d444126ecc05f22c49ff3fbd7d9f4868a000000000000000000000000b994882a1b9bd98a71dd6ea5f61577c42848b0e8{minAmountOut}00000000000000000000000055d398326f99059ff775485246999027b3197955{amountIn}00000000000000000000000000000000000000000000000000000000000000e000000000000000000000000000000000000000000000000000000000000004049aa903560000000000000000000000000000000000000000000000000000000000000000000000000000000000000000b994882a1b9bd98a71dd6ea5f61577c42848b0e800000000000000000000000055d398326f99059ff775485246999027b3197955{amountIn}0000000000000000000000000000000000000000000000000000019990f10bc70000000000000000000000000000000000000000000000000000000000000100000000000000000000000000000000000000000000000000000000000000014000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000001{minAmountOut}000000000000000000000000000000000000000000000000000000000000000100000000000000000000000000000000000000000000000000000000000000200000000000000000000000000000000000000000000000000000000000000001000000000000000000000000000000000000000000000000000000000000002000000000000000000000000000000000000000000000000000000000000000a000000000000000000000000000000000000000000000000000000000000000e000000000000000000000000000000000000000000000000000000000000001200000000000000000000000000000000000000000000000000000000000000160000000000000000000000000b994882a1b9bd98a71dd6ea5f61577c42848b0e80000000000000000000000000000000000000000000000000000000000000001020000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000010000000000000000000000005efc784d444126ecc05f22c49ff3fbd7d9f4868a0000000000000000000000000000000000000000000000000000000000000001000000000000000000002710cf39e58ed4decc9ef7bdb2f9335602dc22be99b40000000000000000000000000000000000000000000000000000000000000001000000000000000000000000000000000000000000000000000000000000002000000000000000000000000000000000000000000000000000000000000000800000000000000000000000000000000000000000000000000000000000000000000000000000000000000000b994882a1b9bd98a71dd6ea5f61577c42848b0e800000000000000000000000055d398326f99059ff775485246999027b3197955000000000000000000000000000000000000000000000000000000000000006400000000000000000000000000000000000000000000000000000000'
    }
  });

  const [status, setStatus] = useState<string>('');
  const [txHash, setTxHash] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [useAtomicExecution, setUseAtomicExecution] = useState<boolean>(true);
  const [isAAWallet, setIsAAWallet] = useState<boolean>(false);

  // Check if wallet is already connected on page load
  useEffect(() => {
    checkWalletConnection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 检测是否为账户抽象钱包（包含MetaMask Smart Account支持）
  const detectAAWallet = async (provider: BrowserProvider): Promise<boolean> => {
    try {
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      const code = await provider.getCode(address);

      // 如果地址有合约代码，可能是智能合约钱包
      if (code !== '0x') {
        console.log('Contract wallet detected at address:', address);
        setIsAAWallet(true);
        return true;
      }

      // 检查MetaMask的智能账户功能
      if (window.ethereum && window.ethereum.isMetaMask) {
        console.log('Checking MetaMask smart account features...');

        try {
          // 首先检查钱包能力 (EIP-5792)
          const capabilities = await (window.ethereum as any).request({
            method: 'wallet_getCapabilities',
            params: [address]
          }).catch(() => null);

          console.log('Wallet capabilities:', capabilities);

          // 检查是否支持批量调用 (EIP-5792)
          const chainId = `0x${BSC_CHAIN_ID.toString(16)}`;
          const chainCapabilities = capabilities?.[chainId] || capabilities?.['0x1'] || {};

          if (chainCapabilities.atomicBatch ||
              chainCapabilities.paymasterService ||
              chainCapabilities.sendCalls ||
              chainCapabilities['wallet_sendCalls']) {
            console.log('MetaMask supports EIP-5792 batch capabilities');
            setIsAAWallet(true);
            return true;
          }

          // 检查MetaMask版本和Smart Account支持
          const metamaskVersion = (window.ethereum as any)._metamask?.version;
          console.log('MetaMask version:', metamaskVersion);

          // 检查是否为MetaMask的Smart Account模式
          const hasSmartAccount = (window.ethereum as any).isSmartAccount ||
                                 (window.ethereum as any).smartAccount ||
                                 (window.ethereum as any)._metamask?.smartAccount;

          if (hasSmartAccount) {
            console.log('MetaMask Smart Account detected');
            setIsAAWallet(true);
            return true;
          }

          // 检查MetaMask Snaps支持 (可能启用批量交易功能)
          try {
            const snapsSupported = await (window.ethereum as any).request({
              method: 'wallet_getSnaps'
            }).catch(() => null);

            if (snapsSupported && Object.keys(snapsSupported).length > 0) {
              console.log('MetaMask Snaps detected, may support batch transactions');
              // 不自动设置为AA，但可以尝试批量交易
            }
          } catch (snapError) {
            console.log('Snaps check failed:', snapError);
          }

          // 检查是否支持实验性批量交易方法
          try {
            // 尝试调用批量交易方法来检测支持
            await (window.ethereum as any).request({
              method: 'wallet_sendCalls',
              params: [{
                version: '1.0',
                chainId: chainId,
                from: address,
                calls: [] // 空调用用于测试
              }]
            }).catch((error: any) => {
              // 如果返回参数错误而不是方法不存在，说明支持该方法
              if (error.code === -32602 || error.message.includes('Invalid params')) {
                console.log('wallet_sendCalls method is supported');
                setIsAAWallet(true);
                return true;
              }
              throw error;
            });
          } catch (testError: any) {
            if (testError.code !== -32601) { // -32601 = Method not found
              console.log('wallet_sendCalls may be supported');
            }
          }

        } catch (metaMaskError) {
          console.log('MetaMask capabilities check failed:', metaMaskError);
        }

        // MetaMask普通模式，不是AA钱包
        console.log('MetaMask detected as regular EOA wallet');
        setIsAAWallet(false);
        return false;
      }

      // 检查其他钱包类型标识
      if (window.ethereum) {
        const walletName = window.ethereum.isSafe ? 'Safe' :
                          window.ethereum.isArgent ? 'Argent' :
                          window.ethereum.isBiconomy ? 'Biconomy' :
                          window.ethereum.isAmbire ? 'Ambire' :
                          'Unknown';

        console.log('Detected wallet:', walletName);

        const isAA = SUPPORTED_AA_WALLETS.some(name =>
          walletName.includes(name) || (window.ethereum as any)[`is${name}`]
        );

        setIsAAWallet(isAA);
        return isAA;
      }

      setIsAAWallet(false);
      return false;
    } catch (error) {
      console.log('Failed to detect AA wallet:', error);
      setIsAAWallet(false);
      return false;
    }
  };

  const checkWalletConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const provider = new BrowserProvider(window.ethereum);
        const accounts = await provider.listAccounts();

        if (accounts.length > 0) {
          const network = await provider.getNetwork();
          const signer = await provider.getSigner();

          setWallet({
            connected: true,
            address: accounts[0].address,
            network: network.name,
            provider,
            signer
          });

          // 检测是否为账户抽象钱包
          await detectAAWallet(provider);

          if (Number(network.chainId) !== BSC_CHAIN_ID) {
            setStatus(`Please switch to BSC Mainnet (Chain ID: ${BSC_CHAIN_ID})`);
          }
        }
      } catch (error) {
        console.error('Error checking wallet connection:', error);
      }
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      setStatus('Please install MetaMask or Binance Chain Wallet');
      return;
    }

    try {
      setLoading(true);
      setStatus('Connecting wallet...');

      // Request account access
      await window.ethereum.request({ method: 'eth_requestAccounts' });

      const provider = new BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      const signer = await provider.getSigner();
      const address = await signer.getAddress();

      if (Number(network.chainId) !== BSC_CHAIN_ID) {
        // Try to switch to BSC network
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: `0x${BSC_CHAIN_ID.toString(16)}` }],
          });
        } catch (switchError: any) {
          // If the chain hasn't been added to MetaMask
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: `0x${BSC_CHAIN_ID.toString(16)}`,
                chainName: 'Binance Smart Chain',
                nativeCurrency: {
                  name: 'BNB',
                  symbol: 'BNB',
                  decimals: 18,
                },
                rpcUrls: [BSC_RPC_URL],
                blockExplorerUrls: ['https://bscscan.com/'],
              }],
            });
          }
        }

        // Re-check network after switch
        const newNetwork = await provider.getNetwork();
        if (Number(newNetwork.chainId) !== BSC_CHAIN_ID) {
          setStatus(`Please switch to BSC Mainnet (Chain ID: ${BSC_CHAIN_ID})`);
          setLoading(false);
          return;
        }
      }

      setWallet({
        connected: true,
        address,
        network: 'Binance Smart Chain',
        provider,
        signer
      });

      // 检测是否为账户抽象钱包
      await detectAAWallet(provider);

      setStatus('Wallet connected successfully');
      setLoading(false);
    } catch (error: any) {
      setStatus(`Error connecting wallet: ${error.message}`);
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTransactionChange = (
    transaction: 'transactionA' | 'transactionB',
    field: keyof TransactionTemplate,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [transaction]: {
        ...prev[transaction],
        [field]: value
      }
    }));
  };

  const replaceDataPlaceholders = (dataTemplate: string, amountIn: string, minAmountOut: string, isSecondTransaction: boolean = false): string => {
    try {
      // console.log("dataTemplate:", dataTemplate)
      // Convert input amounts to BigNumber and then to 32-byte hex
      const amountInBN = parseUnits(amountIn, 0); // Assuming no decimals, adjust if needed
      const minAmountOutBN = parseUnits(minAmountOut, 0);

      // For second transaction, add 0.1% slippage to the minimum output
      // Second transaction uses minAmountOut as input, and needs amountIn + slippage as minimum output
      let adjustedAmountInBN = amountInBN;
      if (isSecondTransaction) {
        // Add 0.1% slippage (multiply by 1.001)
        adjustedAmountInBN = amountInBN * BigInt(990) / BigInt(1000);
      }

      // Convert to 32-byte hex (64 characters + 0x prefix)
      const amountInHex = zeroPadValue(toBeHex(amountInBN), 32);
      const minAmountOutHex = zeroPadValue(toBeHex(minAmountOutBN), 32);
      const adjustedAmountInHex = zeroPadValue(toBeHex(adjustedAmountInBN), 32);

      // Replace placeholders (remove 0x prefix for replacement)
      // Support both formats: {amountIn}/{minAmountOut} and {AMOUNT_IN}/{AMOUNT_OUT}
      let result = dataTemplate.replace(/{amountIn}/g, isSecondTransaction ? adjustedAmountInHex.slice(2) : amountInHex.slice(2));
      result = result.replace(/{minAmountOut}/g, isSecondTransaction ? minAmountOutHex.slice(2) : minAmountOutHex.slice(2));
      // result = result.replace(/{AMOUNT_IN}/g, isSecondTransaction ? minAmountOutHex.slice(2) : amountInHex.slice(2));
      // result = result.replace(/{AMOUNT_OUT}/g, isSecondTransaction ? adjustedAmountInHex.slice(2) : minAmountOutHex.slice(2));

      // console.log("amountInBN:", amountInBN.toString(), ";adjustedAmountInBN:", adjustedAmountInBN.toString(), ";minAmountOutBN:", minAmountOutBN.toString())
      // console.log("result:", result)
      return result;
    } catch (error) {
      throw new Error(`Error replacing placeholders: ${error}`);
    }
  };

  const executeAtomicTransaction = async () => {
    if (!wallet.connected || !wallet.signer) {
      setStatus('Please connect your wallet first');
      return;
    }

    if (!formData.amountIn || !formData.minAmountOut) {
      setStatus('Please enter both amountIn and minAmountOut');
      return;
    }

    if (!formData.transactionA.data || !formData.transactionB.data) {
      setStatus('Please enter transaction data templates');
      return;
    }

    try {
      setLoading(true);
      setStatus('Preparing atomic transaction...');

      // Replace placeholders in data templates
      const dataA = replaceDataPlaceholders(
        formData.transactionA.data,
        formData.amountIn,
        formData.minAmountOut,
        false // First transaction
      );

      const dataB = replaceDataPlaceholders(
        formData.transactionB.data,
        formData.amountIn,
        formData.minAmountOut,
        true // Second transaction with slippage
      );

      if (useAtomicExecution && isAAWallet) {
        // 使用账户抽象钱包的批量交易功能
        setStatus('Preparing batch transaction with Account Abstraction...');

        try {
          // 构建批量交易数据
          const batchTransactions = [
            {
              to: formData.transactionA.target,
              data: dataA,
              value: '0',
              gasLimit: 500000
            },
            {
              to: formData.transactionB.target,
              data: dataB,
              value: '0',
              gasLimit: 500000
            }
          ];

          setStatus('Sending batch transaction...');

          // 尝试使用不同的AA钱包API
          let tx;

          if (window.ethereum.isMetaMask) {
            console.log("Using MetaMask batch transaction capabilities");

            try {
              // 尝试使用MetaMask批量交易功能
              console.log("Attempting MetaMask batch transaction");

              let txResult;
              const chainId = `0x${BSC_CHAIN_ID.toString(16)}`;

              // 首先尝试使用EIP-5792标准的wallet_sendCalls
              try {
                const callsParams = {
                  version: '2.0.0', // 使用MetaMask要求的2.0.0版本
                  chainId: chainId,
                  from: wallet.address,
                  atomicRequired: true, // 要求原子性执行
                  calls: [
                    {
                      to: formData.transactionA.target,
                      data: dataA,
                      value: '0x0'
                    },
                    {
                      to: formData.transactionB.target,
                      data: dataB,
                      value: '0x0'
                    }
                  ]
                };

                console.log("Trying wallet_sendCalls with params:", callsParams);
                txResult = await (window.ethereum as any).request({
                  method: 'wallet_sendCalls',
                  params: [callsParams]
                });
                console.log("wallet_sendCalls successful:", txResult);

                setStatus('MetaMask batch transaction completed successfully!');
                setTxHash(txResult?.bundleId || txResult?.transactionHash || JSON.stringify(txResult));

              } catch (sendCallsError: any) {
                console.log("wallet_sendCalls failed:", sendCallsError);

                // 如果wallet_sendCalls失败，尝试传统的批量交易方法
                if (sendCallsError.code === -32601) {
                  console.log("wallet_sendCalls not supported, trying eth_sendTransactionBatch");

                  const batchTransactions = [
                    {
                      to: formData.transactionA.target,
                      data: dataA,
                      value: '0x0',
                      gasLimit: '0x7A120'
                    },
                    {
                      to: formData.transactionB.target,
                      data: dataB,
                      value: '0x0',
                      gasLimit: '0x7A120'
                    }
                  ];

                  try {
                    txResult = await (window.ethereum as any).request({
                      method: 'eth_sendTransactionBatch',
                      params: [batchTransactions]
                    });
                    console.log("eth_sendTransactionBatch successful:", txResult);

                    setStatus('MetaMask batch transaction completed successfully!');
                    setTxHash(Array.isArray(txResult) ? txResult.join(', ') : txResult);

                  } catch (batchError: any) {
                    console.log("eth_sendTransactionBatch also failed:", batchError);
                    throw new Error(`Batch transaction methods not supported: ${batchError.message}`);
                  }
                } else {
                  // 其他错误，重新抛出
                  throw sendCallsError;
                }
              }

            } catch (metaMaskError: any) {
              console.log("MetaMask batch transaction failed, falling back to sequential:", metaMaskError);
              setStatus(`批量交易失败: ${metaMaskError.message}，切换到顺序执行模式...`);
              throw new Error('MetaMask batch operations failed, using sequential execution');
            }
          } else if ((window.ethereum as any).isSafe) {
            console.log("Using Gnosis Safe batch transaction");

            const safeBatchTransactions = batchTransactions.map(tx => ({
              to: tx.to,
              value: tx.value,
              data: tx.data,
              operation: 0 // CALL operation
            }));

            try {
              // Gnosis Safe API
              tx = await (window.ethereum as any).sendBatch(safeBatchTransactions);
              console.log("Gnosis Safe batch transaction successful:", tx);
            } catch (safeError) {
              console.log("Gnosis Safe sendBatch failed, trying alternative API");
              // 尝试使用Safe的替代API
              tx = await (window.ethereum as any).request({
                method: 'gs_multi_send',
                params: [safeBatchTransactions]
              });
            }
          } else if ((window.ethereum as any).sendBatchTransaction) {
            console.log("Using generic batch transaction API");
            // 通用批量交易API
            tx = await (window.ethereum as any).sendBatchTransaction(batchTransactions);
          } else if ((window.ethereum as any).isArgent) {
            console.log("Using Argent wallet batch transaction");
            // Argent钱包批量交易
            tx = await (window.ethereum as any).request({
              method: 'wallet_sendCalls',
              params: [{
                version: '2.0.0',
                chainId: `0x${BSC_CHAIN_ID.toString(16)}`,
                from: wallet.address,
                atomicRequired: true,
                calls: batchTransactions.map(tx => ({
                  to: tx.to,
                  data: tx.data,
                  value: tx.value
                }))
              }]
            });
          } else if ((window.ethereum as any).isBiconomy) {
            console.log("Using Biconomy Smart Account batch transaction");
            // Biconomy Smart Account批量交易
            const biconomyBatch = {
              to: batchTransactions.map(tx => tx.to),
              data: batchTransactions.map(tx => tx.data),
              value: batchTransactions.map(tx => tx.value)
            };
            tx = await (window.ethereum as any).sendUserOperation(biconomyBatch);
          } else {
            console.log("No specific batch API found, trying generic EIP-5792");
            // 尝试通用的EIP-5792标准
            try {
              tx = await (window.ethereum as any).request({
                method: 'wallet_sendCalls',
                params: [{
                  version: '2.0.0',
                  chainId: `0x${BSC_CHAIN_ID.toString(16)}`,
                  from: wallet.address,
                  atomicRequired: true,
                  calls: batchTransactions.map(tx => ({
                    to: tx.to,
                    data: tx.data,
                    value: tx.value
                  }))
                }]
              });
              console.log("Generic EIP-5792 batch transaction successful:", tx);
            } catch (genericError) {
              console.log("Generic batch API also failed, falling back to sequential");
              throw new Error('AA wallet does not support batch transactions, falling back to sequential mode');
            }
          }

          if (typeof tx === 'string') {
            setTxHash(tx);
          } else if (tx.hash) {
            setTxHash(tx.hash);
          } else {
            setTxHash(JSON.stringify(tx));
          }

          setStatus('Batch transaction completed successfully!');
        } catch (aaError) {
          console.warn('AA batch transaction failed, falling back to sequential:', aaError);
          setStatus('AA batch failed, executing sequentially...');

          // 回退到顺序执行
          const tx1 = await wallet.signer.sendTransaction({
            to: formData.transactionA.target,
            data: dataA,
            gasLimit: 500000
          });

          setStatus('First transaction sent, waiting for confirmation...');
          await tx1.wait();

          const tx2 = await wallet.signer.sendTransaction({
            to: formData.transactionB.target,
            data: dataB,
            gasLimit: 500000
          });

          setStatus('Second transaction sent, waiting for confirmation...');
          await tx2.wait();

          setTxHash(`${tx1.hash}, ${tx2.hash}`);
          setStatus('Sequential transactions completed successfully!');
        }
      } else {
        // 顺序发送两个独立交易（原有逻辑）
        setStatus('Sending transactions sequentially...');

        // const tx1 = await wallet.signer.sendTransaction({
        //   to: formData.transactionA.target,
        //   data: dataA,
        //   gasLimit: 500000
        // });
        //
        // setStatus('First transaction sent, waiting for confirmation...');
        //
        // const tx2 = await wallet.signer.sendTransaction({
        //   to: formData.transactionB.target,
        //   data: dataB,
        //   gasLimit: 500000
        // });

        const tx2Op = wallet.signer.sendTransaction({
          to: formData.transactionB.target,
          data: dataB,
          gasLimit: 500000
        });
        
        const tx1Op = wallet.signer.sendTransaction({
          to: formData.transactionA.target,
          data: dataA,
          gasLimit: 500000
        });

        setStatus('First transaction sent, waiting for confirmation...');


        const tx1 = await tx1Op;
        const tx2 = await tx2Op;

        setStatus('Second transaction sent, waiting for confirmation...');
        await tx1.wait();
        await tx2.wait();

        setTxHash(`${tx1.hash}, ${tx2.hash}`);
        setStatus('Sequential transactions completed successfully!');
      }
      setLoading(false);

    } catch (error: any) {
      setStatus(`Transaction failed: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>BSC Atomic Transaction Tool</h1>

        {/* Wallet Connection Section */}
        <div className="wallet-section">
          {!wallet.connected ? (
            <button
              onClick={connectWallet}
              disabled={loading}
              className="connect-button"
            >
              {loading ? 'Connecting...' : 'Connect Wallet'}
            </button>
          ) : (
            <div className="wallet-info">
              <p><strong>Connected:</strong> {wallet.address}</p>
              <p><strong>Network:</strong> {wallet.network}</p>
              <p><strong>Wallet Type:</strong> {isAAWallet ? '🔒 Account Abstraction (Smart Contract)' : window.ethereum?.isMetaMask ? '📱 MetaMask (EOA - 原子执行将回退到顺序模式)' : '📱 EOA (Externally Owned Account)'}</p>
            </div>
          )}
        </div>

        {/* Input Form */}
        {wallet.connected && (
          <div className="form-section">
            <div className="input-group">
              <label>Amount In:</label>
              <input
                type="text"
                value={formData.amountIn}
                onChange={(e) => handleInputChange('amountIn', e.target.value)}
                placeholder="Enter amount in"
              />
            </div>

            <div className="input-group">
              <label>Min Amount Out:</label>
              <input
                type="text"
                value={formData.minAmountOut}
                onChange={(e) => handleInputChange('minAmountOut', e.target.value)}
                placeholder="Enter minimum amount out"
              />
            </div>

            <div className="slippage-info">
              <p>📊 <strong>滑点设置:</strong> 第二个交易自动添加 0.1% 滑点保护</p>
            </div>

            <div className="execution-mode">
              <label className="switch-label">
                <strong>⚡ 执行模式:</strong>
                <input
                  type="checkbox"
                  checked={useAtomicExecution}
                  onChange={(e) => setUseAtomicExecution(e.target.checked)}
                  className="execution-switch"
                />
                <span className="switch-text">
                  {useAtomicExecution ? '原子执行 (推荐)' : '顺序执行'}
                </span>
              </label>
              <p className="mode-description">
                {useAtomicExecution
                  ? isAAWallet
                    ? '🔒 使用账户抽象钱包批量执行，确保原子性和gas优化'
                    : window.ethereum?.isMetaMask
                      ? '📱 MetaMask暂不支持批量交易，将自动使用顺序执行模式'
                      : '🔗 原子执行模式已启用，但当前钱包不支持批量交易，将回退到顺序执行'
                  : '⚠️ 两个交易将顺序执行，可能存在中间状态风险'
                }
              </p>
            </div>

            <div className="transaction-section">
              <h3>Transaction A</h3>
              <div className="input-group">
                <label>Target Address:</label>
                <input
                  type="text"
                  value={formData.transactionA.target}
                  onChange={(e) => handleTransactionChange('transactionA', 'target', e.target.value)}
                  placeholder="0x..."
                />
              </div>
              <div className="input-group">
                <label>Data Template:</label>
                <textarea
                  value={formData.transactionA.data}
                  onChange={(e) => handleTransactionChange('transactionA', 'data', e.target.value)}
                  placeholder="0x... (use {amountIn} and {minAmountOut} as placeholders)"
                  rows={3}
                />
              </div>
            </div>

            <div className="transaction-section">
              <h3>Transaction B</h3>
              <div className="input-group">
                <label>Target Address:</label>
                <input
                  type="text"
                  value={formData.transactionB.target}
                  onChange={(e) => handleTransactionChange('transactionB', 'target', e.target.value)}
                  placeholder="0x..."
                />
              </div>
              <div className="input-group">
                <label>Data Template:</label>
                <textarea
                  value={formData.transactionB.data}
                  onChange={(e) => handleTransactionChange('transactionB', 'data', e.target.value)}
                  placeholder="0x... (use {amountIn} and {minAmountOut} as placeholders)"
                  rows={3}
                />
              </div>
            </div>

            <button
              onClick={executeAtomicTransaction}
              disabled={loading}
              className="execute-button"
            >
              {loading ? 'Executing...' : 'Enter'}
            </button>
          </div>
        )}

        {/* Status Section */}
        <div className="status-section">
          {status && <p className="status">{status}</p>}
          {txHash && (
            <div className="tx-hash">
              <p><strong>Transaction Hash(es):</strong></p>
              <p className="hash">{txHash}</p>
            </div>
          )}
        </div>
      </header>
    </div>
  );
}

declare global {
  interface Window {
    ethereum?: any;
  }
}

export default App;
